


























import { defineComponent } from "@vue/composition-api";
import { parseISO, isSameDay } from "date-fns";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import { applyOnElement } from "@/lib/elementHelpers";
import { formatDate } from "@/lib/time";
import { BlockingStatus } from "@/types";
import InfiniteScroll from "@/components/InfiniteScroll";
import MessageItem from "@/components/MessageItem";

export default defineComponent({
  name: "MessageList",
  components: { MessageItem, InfiniteScroll },
  props: {
    authUserId: { type: String, required: true },
    messages: { type: Array as () => Message[], required: true },
    minUnreadMessageId: { type: String, required: true },
    enableScroll: { type: Boolean, default: false },
    userId: { type: String, required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    blockingStatus: { type: Object as () => BlockingStatus, required: true },
    canManageMessages: { type: Boolean, required: true },
  },
  data() {
    return {
      blurredMessages: [] as Message[],
    };
  },
  computed: {
    observer(): IntersectionObserver {
      return new IntersectionObserver(this.onObserve, {
        rootMargin: "-64px 0px -56px 0px",
      });
    },
    blurOpponentObserver(): IntersectionObserver {
      return new IntersectionObserver(this.onOpponentMessageObserve);
    },
  },
  methods: {
    date(date: string) {
      return formatDate(new Date(date), { month: "long", day: "2-digit", year: "numeric" });
    },

    isNextDayMessage(messages: Message[], index: number) {
      if (index === 0) return true;
      return !isSameDay(parseISO(messages[index - 1].time), parseISO(messages[index].time));
    },

    isUnreadMessage(message: Message) {
      if (this.minUnreadMessageId === "0") return false;
      return Number(message.id) >= Number(this.minUnreadMessageId);
    },

    isOpponentMessage(message: Message) {
      return this.authUserId !== message.senderId;
    },

    isOpponentMember(message: Message) {
      return checkRole(message.senderUser?.role || "", "member");
    },

    onOpponentMessageClick(message: Message, el: Element) {
      const index = this.blurredMessages.findIndex(item => item === message);
      if (index > -1) {
        this.blurredMessages.splice(index, 1);
      }
      this.blurOpponentObserver.unobserve(el);
    },

    isMessageBlurred(message: Message) {
      return !!this.blurredMessages.find(item => item.id === message.id);
    },

    onOpponentMessageObserve(entries: IntersectionObserverEntry[]) {
      entries.forEach(entry => {
        if (!entry) return;
        const id = entry.target.id.split("message-")[1];
        const message = this.messages.find(i => i.id === id);
        const index = this.blurredMessages.findIndex(i => i.id === id);
        if (entry.isIntersecting && message) {
          this.blurredMessages.push(message);
        } else {
          if (index > -1) {
            this.blurredMessages.splice(index, 1);
          }
        }
      });
    },

    async setObserve({ el }: { el: Element }) {
      this.observer.observe(el);
      const id = el.id.split("message-")[1];
      const message = this.messages.find(i => i.id === id);
      if (message && !!message.attachment.length && this.isOpponentMessage(message) && this.isOpponentMember(message)) {
        this.blurOpponentObserver.observe(el);
      }
    },

    stopObserve() {
      const observer = this.observer;
      if (this.$refs.unreadMessages && observer) {
        applyOnElement(this.$refs.unreadMessages, el => {
          if (el instanceof Element) observer.unobserve(el);
        });
      }
    },

    onObserve(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
      entries.forEach(entry => {
        if (entry && entry.isIntersecting) {
          const id = Number(entry.target.id.split("message-")[1]);
          const minUnreadMessageId = Number(this.minUnreadMessageId);
          if (minUnreadMessageId > 0 && minUnreadMessageId <= id) {
            const latestReadMessage = this.messages.find(message => message.id === id.toString());
            latestReadMessage && this.$emit("markRead", latestReadMessage);
          }
          observer.unobserve(entry.target);
        }
      });
    },
  },
  destroyed() {
    this.stopObserve();
    this.observer && this.observer.disconnect();
    this.blurOpponentObserver && this.blurOpponentObserver.disconnect();
  },
});
